import { useEffect, useRef, useCallback } from 'react';

interface IdleTimeoutOptions {
  timeout?: number;
  onIdle: () => void;
  events?: string[];
  enabled?: boolean;
}

export const useIdleTimeout = ({
  timeout = 15 * 60 * 1000,
  onIdle,
  events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
  enabled = true,
}: IdleTimeoutOptions) => {
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const lastActivityTime = useRef<number>(Date.now());

  const clearTimer = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearTimer();
    lastActivityTime.current = Date.now();

    timeoutId.current = setTimeout(() => {
      onIdle();
    }, timeout);
  }, [enabled, timeout, onIdle, clearTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    resetTimer();

    events.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      clearTimer();
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [enabled, events, resetTimer, clearTimer, timeout]);

  return {
    resetTimer,
    lastActivityTime: lastActivityTime.current,
  };
};
